import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { CompactAvailabilityBlockDto, CompactAvailabilityConfigDto } from '../../../models/court/dto/batch-upsert-availabilities-compact.dto';
import { CourtAvailabilityTemplateRuleDto } from '../../../models/court/dto/court-availability-template.dto';
import { MINUTES_PER_SLOT, transformInputToRule } from '../../../models/court/dto/court-availability-template.constants';
import { SportType } from '../../../models/court/dto/court.dto';
import {
    PricingGridState,
    blockRangeMinutes,
    flattenBlocksToSlots,
    parseBlockPrice,
    resolveCellPrice,
} from '../model';

export type BuildGridCompactBlocksInput = {
    state: PricingGridState;
    /** The court's sports; every priced cell is priced for each of them. */
    sports: SportType[];
    currency: Currency;
    /**
     * Mirrors the compact endpoint's flag. When OFF the server splits every
     * block into back-to-back slots of its config's duration, aligned to the
     * block start — so a block may only span several grid cells when they
     * are contiguous AND the display interval equals the slot duration (the
     * split then reproduces the cells exactly). When ON the server stores
     * each block as one availability covering its whole range, and
     * bookings of the config's duration may start every 15 minutes inside
     * it, so contiguous same-price cells can always be merged.
     */
    returnAsOneChunk: boolean;
};

type DayBlock = {
    day: number;
    startMinute: number;
    endMinute: number;
    blockId: string;
    configs: CompactAvailabilityConfigDto[];
    signature: string;
};

const MS_PER_MINUTE = 60_000;

function configsForCell(
    input: BuildGridCompactBlocksInput,
    day: number,
    slot: { blockId: string; startMinute: number; endMinute: number },
): CompactAvailabilityConfigDto[] {
    const slotDurationInMs = (slot.endMinute - slot.startMinute) * MS_PER_MINUTE;
    const configs: CompactAvailabilityConfigDto[] = [];
    for (const sport of input.sports) {
        const price = resolveCellPrice(input.state, sport, day, slot);
        if (price === null) continue;
        configs.push({ sport, slotDurationInMs, price, currency: input.currency });
    }
    return configs;
}

function signatureOf(configs: CompactAvailabilityConfigDto[]): string {
    return configs.map((c) => `${c.sport}=${c.price}`).join(',');
}

/**
 * Turns the grid into the compact blocks `POST courts/:id/availabilities/compact`
 * and `POST courts` (`compactAvailabilities`) accept.
 *
 * One block per priced cell is the faithful translation (it also makes a
 * display interval that differs from the slot duration work, e.g. 90-minute
 * slots offered every 60 minutes become overlapping availabilities the
 * booking search already copes with). Contiguous cells with identical
 * prices are merged when the server's expansion would rebuild them
 * identically (see `returnAsOneChunk` above), and identical day-blocks are
 * then folded across weekdays, so a uniform week collapses to a handful of
 * blocks instead of a hundred.
 *
 * Cleared cells (and cells whose block has no flat price) produce nothing:
 * that slot is simply not bookable that day.
 */
export function buildGridCompactBlocks(input: BuildGridCompactBlocksInput): CompactAvailabilityBlockDto[] {
    const slots = flattenBlocksToSlots(input.state.blocks);
    const dayBlocks: DayBlock[] = [];

    for (let day = 0; day < 7; day++) {
        let current: DayBlock | null = null;
        for (const slot of slots) {
            const configs = configsForCell(input, day, slot);
            if (configs.length === 0) {
                current = null;
                continue;
            }
            const signature = signatureOf(configs);
            const block = input.state.blocks.find((b) => b.id === slot.blockId);
            // One chunk: any touching or overlapping cell extends the run (the
            // chunk offers every 15-minute start anyway). Split mode: only a
            // cell that starts exactly where the run ends, and only when the
            // server's duration-aligned split reproduces the cells.
            const joins = input.returnAsOneChunk
                ? current !== null && slot.startMinute <= current.endMinute
                : current !== null &&
                  current.endMinute === slot.startMinute &&
                  block !== undefined &&
                  block.intervalMinutes === block.durationMinutes;
            if (current && joins && current.blockId === slot.blockId && current.signature === signature) {
                current.endMinute = Math.max(current.endMinute, slot.endMinute);
                continue;
            }
            current = { day, startMinute: slot.startMinute, endMinute: slot.endMinute, blockId: slot.blockId, configs, signature };
            dayBlocks.push(current);
        }
    }

    // Fold identical (hours + configs) day-blocks across weekdays.
    const folded = new Map<string, CompactAvailabilityBlockDto>();
    for (const dayBlock of dayBlocks) {
        const key = `${dayBlock.startMinute}|${dayBlock.endMinute}|${dayBlock.signature}|${dayBlock.configs[0]?.slotDurationInMs}`;
        const existing = folded.get(key);
        if (existing) {
            existing.daysOfWeek.push(dayBlock.day);
            continue;
        }
        folded.set(key, {
            daysOfWeek: [dayBlock.day],
            startMinuteOfDay: dayBlock.startMinute,
            endMinuteOfDay: dayBlock.endMinute,
            configs: dayBlock.configs,
        });
    }
    return Array.from(folded.values());
}

/**
 * The template the court remembers the grid by. The template is the form's
 * memory, never read by the API to price anything (see
 * `court-availability-template.constants.ts`), so it carries only what the
 * grid editor needs to re-seed its blocks: hours, slot duration and the flat
 * price per sport. Per-cell overrides are re-derived from the live
 * availabilities instead (`seed-grid-state.ts`).
 */
export function buildGridTemplateRules(
    state: PricingGridState,
    sports: SportType[],
    currency: Currency,
    makeId: () => string,
): CourtAvailabilityTemplateRuleDto[] {
    return state.blocks.flatMap((block) => {
        const range = blockRangeMinutes(block);
        if (!range) return [];
        const slotDurationInMs = block.durationMinutes * MS_PER_MINUTE;
        const flatPrice = parseBlockPrice(block.priceRaw);
        return [
            transformInputToRule({
                id: block.id,
                daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                min: Math.round(range[0] / MINUTES_PER_SLOT),
                max: Math.round(range[1] / MINUTES_PER_SLOT),
                slotDurationInMs,
                configurations: sports.map((sport) => ({
                    id: makeId(),
                    sport,
                    slotDurationInMs,
                    price: flatPrice ?? undefined,
                    currency,
                })),
            }),
        ];
    });
}

/** How many cells are priced across the whole grid, and how many are not. */
export function countGridCells(state: PricingGridState, sports: SportType[]): { priced: number; unpriced: number } {
    const slots = flattenBlocksToSlots(state.blocks);
    let priced = 0;
    let unpriced = 0;
    for (const sport of sports) {
        for (let day = 0; day < 7; day++) {
            for (const slot of slots) {
                if (resolveCellPrice(state, sport, day, slot) === null) unpriced++;
                else priced++;
            }
        }
    }
    return { priced, unpriced };
}
